import * as React from 'react';

interface LoadableState<T> {
	data: Nullable<T>;
	isLoading: boolean;
}

export function useLoadableState<T>(fetch: () => Promise<Nullable<T>>) {
	const [state, setState] = React.useState<LoadableState<T>>({ data: null, isLoading: true });

	const load = React.useCallback(async () => {
		setState((current) => ({ ...current, isLoading: true }));
		try {
			const loaded = await fetch();
			setState((current) => ({ ...current, data: loaded, isLoading: false }));
		} finally {
			setState((current) => ({ ...current, isLoading: false }));
		}
	}, [fetch]);

	const reset = React.useCallback(() => setState({ data: null, isLoading: true }), []);

	return React.useMemo(() => ({
		data: state.data,
		isLoading: state.data === null || state.isLoading,
		load,
		reset,
	}), [load, reset, state.data, state.isLoading]);
}
